import { useMemo, useState, useEffect, forwardRef, useCallback } from "react";

import classNames from "classnames";

import { isEmpty } from "../../utils/validators";

const TextField = forwardRef((props, ref) => {
  const {
    name,
    label,
    placeholder,
    autoComplete,
    disabled,
    hidden,
    required,
    value,
    error,
    onChange,
    onFocus,
    onBlur,
    onCopy,
    onPaste,
    ...rest
  } = props;

  const [isActive, setIsActive] = useState(false);

  const title = useMemo(() => {
    const content = value || placeholder;
    return [label, content].filter((i) => !isEmpty(i)).join(" - ");
  }, [value, placeholder, label]);

  const isFocusHandler = useCallback(() => {
    const activeElement = document.activeElement;
    if (
      !isEmpty(activeElement) &&
      !isEmpty(ref.current) &&
      ref.current === document.activeElement
    ) {
      setIsActive(true);
    } else {
      setIsActive(!isEmpty(placeholder) || !isEmpty(value));
    }
  }, [placeholder, ref, value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => isFocusHandler(), [value, placeholder]);

  const onFocusHandler = useCallback(
    (evt) => {
      onFocus && onFocus(evt);
      isFocusHandler();
    },
    [onFocus, isFocusHandler]
  );

  const onBlurHandler = useCallback(
    (evt) => {
      onBlur && onBlur(evt);
      isFocusHandler();
    },
    [onBlur, isFocusHandler]
  );

  const onChangeHandler = useCallback(
    (evt) => {
      onChange && onChange(evt);
      isFocusHandler();
    },
    [onChange, isFocusHandler]
  );

  const onCopyHandler = useCallback(
    (evt) => {
      onCopy && onCopy(evt);
    },
    [onCopy]
  );

  const onPasteHandler = useCallback(
    (evt) => {
      onPaste && onPaste(evt);
    },
    [onPaste]
  );

  return (
    <div
      className={classNames("text-field__container", {
        unlabelled: isEmpty(label),
        hidden: hidden,
        disabled: disabled,
      })}
      title={title}
    >
      <div
        className={classNames("text-field__content", {
          invalid: error,
        })}
      >
        {label && (
          <label
            htmlFor={name}
            className={classNames("text-field__label", "smaller", {
              active: isActive,
            })}
          >
            {`${label}${required ? " *" : ""}`}
          </label>
        )}
        <div className="text-field__ellipsis">
          <input
            ref={ref}
            id={name}
            name={name}
            type="text"
            className="text-field__input"
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            hidden={hidden}
            required={required}
            autocomplete={autoComplete}
            {...rest}
            onFocus={(evt) => onFocusHandler(evt)}
            onChange={(evt) => onChangeHandler(evt)}
            onBlur={(evt) => onBlurHandler(evt)}
            onCopy={(evt) => onCopyHandler(evt)}
            onPaste={(evt) => onPasteHandler(evt)}
          ></input>
        </div>
      </div>
      <p
        className={classNames("text-field__error", { show: error })}
        title={error}
      >
        {error}
      </p>
    </div>
  );
});

export default TextField;
